/**
 * Video Carousel Component
 * Autoplay manager that plays videos in sequence with hover interaction support
 */

class VideoLightbox {
  constructor() {
    this.lightbox = document.getElementById("video-lightbox");
    if (!this.lightbox) return;

    this.mainCarousel = document.getElementById("lightbox-main-carousel");
    this.navCarousel = document.getElementById("lightbox-nav-carousel");
    this.isOpen = false;
    this.isMuted = false; // Videos start unmuted
    this.isPlaying = false;
    this.currentIndex = 0;
    this.carouselManagers = []; // Track registered carousel managers
    this.playTimer = null; // Debounce timer for video playback
    this.loadedVideos = new Set(); // Track which videos have been force-loaded
    this.videoReadyHandlers = new Map(); // Track canplay event handlers

    this.setupEventListeners();
  }

  registerCarouselManager(manager) {
    this.carouselManagers.push(manager);
  }

  setupEventListeners() {
    // Close buttons
    this.lightbox
      .querySelectorAll('[data-action="close-lightbox"]')
      .forEach((btn) => {
        btn.addEventListener("click", () => this.close());
      });

    // Escape key
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && this.isOpen) {
        this.close();
      }
    });

    // Listen for slide changes on main carousel
    if (this.mainCarousel) {
      this.mainCarousel.on("render-index:changed", (data) => {
        const newIndex = data.newIndex ?? data.currentIndex ?? 0;

        console.log("******* render-index:changed - ", newIndex);

        // Immediately pause current video
        this.pauseCurrentVideo();
        this.currentIndex = newIndex;

        // Clear any pending play timer
        if (this.playTimer) {
          clearTimeout(this.playTimer);
        }

        // Delay starting new video to let transition settle
        this.playTimer = setTimeout(() => {
          this.playVideoAtIndex(newIndex);
        }, 200);
      });
    }

    // Toggle mute
    this.lightbox
      .querySelectorAll('[data-action="toggle-mute"]')
      .forEach((btn) => {
        btn.addEventListener("click", () => this.toggleMute());
      });

    // Toggle play/pause
    this.lightbox
      .querySelectorAll('[data-action="toggle-play"]')
      .forEach((btn) => {
        btn.addEventListener("click", () => this.togglePlay());
      });
  }

  open(startIndex = 0) {
    this.isOpen = true;
    this.currentIndex = startIndex;
    this.lightbox.classList.remove("hidden");
    document.body.style.overflow = "hidden";

    // Pause all background carousel managers
    this.carouselManagers.forEach((manager) => manager.pause());

    // Reset all videos when opening
    this.pauseAllVideos();

    // Force load videos - current, previous, and next few
    this.forceLoadVideos(startIndex);

    // Go to the clicked slide
    if (this.mainCarousel && typeof startIndex === "number") {
      this.mainCarousel.goToSlide(startIndex);
    }

    // Play the video at that slide
    this.playVideoAtIndex(startIndex);
  }

  /**
   * Force videos to start loading by calling .load() and setting preload="auto"
   * This bypasses mobile browser's lazy loading behavior
   */
  forceLoadVideos(centerIndex) {
    const slides = this.mainCarousel?.querySelectorAll("tarot-slide");
    if (!slides) return;

    const totalSlides = slides.length;

    // Load current slide + 2 before + 3 after (6 total)
    const indicesToLoad = [];
    for (let offset = -2; offset <= 3; offset++) {
      let idx = (centerIndex + offset + totalSlides) % totalSlides;
      indicesToLoad.push(idx);
    }

    indicesToLoad.forEach((idx) => {
      if (this.loadedVideos.has(idx)) return;

      const slide = slides[idx];
      const video = slide?.querySelector("video");
      if (video) {
        // Force the browser to start loading
        video.preload = "auto";
        video.load();
        this.loadedVideos.add(idx);

        console.log(`[VideoLightbox] Force loading video at index ${idx}`);
      }
    });
  }

  playVideoAtIndex(index) {
    this.currentIndex = index;

    // Play the video at the given index
    const slides = this.mainCarousel?.querySelectorAll("tarot-slide");
    const slide = slides?.[index];
    const video = slide?.querySelector("video");
    const poster = slide?.querySelector(".video-poster");

    if (!video) return;

    // Clean up any existing handler for this video
    this.cleanupVideoHandler(video);

    // Check if video has enough data to play
    // readyState: 0=HAVE_NOTHING, 1=HAVE_METADATA, 2=HAVE_CURRENT_DATA, 3=HAVE_FUTURE_DATA, 4=HAVE_ENOUGH_DATA
    const isReady = video.readyState >= 3;

    if (isReady) {
      // Video is ready, play immediately
      this.startVideoPlayback(video, poster);
    } else {
      // Video not ready - wait for canplay
      console.log(`[VideoLightbox] Video ${index} not ready (readyState: ${video.readyState}), waiting...`);

      // Force load this video if not already
      if (!this.loadedVideos.has(index)) {
        video.preload = "auto";
        video.load();
        this.loadedVideos.add(index);
      }

      // Set up canplay handler
      const onCanPlay = () => {
        console.log(`[VideoLightbox] Video ${index} ready to play`);
        this.cleanupVideoHandler(video);

        // Only play if still on this index
        if (this.currentIndex === index && this.isOpen) {
          this.startVideoPlayback(video, poster);
        }
      };

      video.addEventListener("canplay", onCanPlay, { once: true });
      this.videoReadyHandlers.set(video, onCanPlay);

      // Also try to play (some browsers will buffer while playing)
      video.muted = this.isMuted;
      video.play().catch(() => {});
    }

    // Preload adjacent videos for smooth navigation
    this.forceLoadVideos(index);
  }

  startVideoPlayback(video, poster) {
    video.muted = this.isMuted;
    video.play().catch(() => {});
    this.isPlaying = true;
    this.updatePlayIcon();

    // Hide poster
    if (poster) {
      poster.classList.add("opacity-0");
    }
  }

  cleanupVideoHandler(video) {
    const handler = this.videoReadyHandlers.get(video);
    if (handler) {
      video.removeEventListener("canplay", handler);
      this.videoReadyHandlers.delete(video);
    }
  }

  pauseCurrentVideo() {
    const video = this.getCurrentVideo();
    if (video) {
      video.pause();
      this.isPlaying = false;
      this.updatePlayIcon();
    }
  }

  toggleMute() {
    this.isMuted = !this.isMuted;

    // Update all videos in lightbox
    this.lightbox.querySelectorAll("video").forEach((video) => {
      video.muted = this.isMuted;
    });

    // Toggle icon visibility
    const muteIcon = this.lightbox.querySelector("[data-mute-icon]");
    const unmuteIcon = this.lightbox.querySelector("[data-unmute-icon]");
    muteIcon?.classList.toggle("hidden", this.isMuted);
    unmuteIcon?.classList.toggle("hidden", !this.isMuted);
  }

  togglePlay() {
    const currentVideo = this.getCurrentVideo();
    if (!currentVideo) return;

    if (currentVideo.paused) {
      currentVideo.play().catch(() => {});
      this.isPlaying = true;
    } else {
      currentVideo.pause();
      this.isPlaying = false;
    }

    this.updatePlayIcon();
  }

  getCurrentVideo() {
    const slides = this.mainCarousel?.querySelectorAll("tarot-slide");
    const slide = slides?.[this.currentIndex];
    return slide?.querySelector("video");
  }

  updatePlayIcon() {
    const playIcon = this.lightbox.querySelector("[data-play-icon]");
    const pauseIcon = this.lightbox.querySelector("[data-pause-icon]");
    playIcon?.classList.toggle("hidden", this.isPlaying);
    pauseIcon?.classList.toggle("hidden", !this.isPlaying);
  }

  pauseAllVideos() {
    this.lightbox.querySelectorAll("tarot-slide").forEach((slide) => {
      const video = slide.querySelector("video");
      const poster = slide.querySelector(".video-poster");

      if (video) {
        // Clean up any pending canplay handlers
        this.cleanupVideoHandler(video);
        video.pause();
        video.currentTime = 0;
      }
      // Show poster when video pauses
      if (poster) {
        poster.classList.remove("opacity-0");
      }
    });
  }

  close() {
    this.isOpen = false;
    this.lightbox.classList.add("hidden");
    document.body.style.overflow = "";

    // Clear any pending play timer
    if (this.playTimer) {
      clearTimeout(this.playTimer);
      this.playTimer = null;
    }

    // Clean up all video handlers
    this.videoReadyHandlers.forEach((handler, video) => {
      video.removeEventListener("canplay", handler);
    });
    this.videoReadyHandlers.clear();

    this.pauseAllVideos();

    // Resume all background carousel managers
    this.carouselManagers.forEach((manager) => manager.resume());
  }
}

class VideoCarouselManager {
  constructor(section) {
    this.section = section;
    this.carousel = section.querySelector("tarot-carousel");
    this.slides = Array.from(section.querySelectorAll(".video-slide"));
    this.currentIndex = 0;
    this.autoplayTimer = null;
    this.duration = (parseInt(section.dataset.autoplayDuration) || 4) * 1000;
    this.isPaused = false;
    this.isVisible = false; // Start false, let IntersectionObserver determine visibility
    this.isHovering = false;
    this.hasStarted = false; // Track if we've started playing for the first time
  }

  init() {
    console.log("[VideoCarousel] init", {
      carousel: this.carousel,
      slidesCount: this.slides.length,
      duration: this.duration,
    });

    if (this.slides.length <= 1) return;

    // Preload first two videos so they're ready when section becomes visible
    this.preloadVideos(2);

    // Set up hover and click listeners on each slide
    this.slides.forEach((slide, index) => {
      slide.addEventListener("mouseenter", () => this.onSlideHover(index));
      slide.addEventListener("mouseleave", () => this.onSlideLeave());
      slide.addEventListener("click", () => this.onSlideClick(index));
    });

    // Listen for carousel slide changes (from drag/swipe)
    if (this.carousel) {
      console.log("[VideoCarousel] Setting up carousel event listener");
      this.carousel.on("render-index:changed", (data) => {
        console.log("[VideoCarousel] render-index:changed", data);
        const newIndex = data.newIndex ?? data.currentIndex ?? 0;
        this.currentIndex = newIndex;
        if (!this.isHovering) {
          this.pauseAllVideos();
          this.playVideoAt(this.currentIndex);
        }
      });
    }

    // Set up IntersectionObserver for viewport visibility
    // This will trigger playback when section scrolls into view
    this.setupVisibilityObserver();
  }

  preloadVideos(count) {
    for (let i = 0; i < Math.min(count, this.slides.length); i++) {
      const slide = this.slides[i];
      const video = slide?.querySelector("video");
      if (video) {
        video.preload = "auto";
        video.load();
        console.log(`[VideoCarousel] Preloading video at index ${i}`);
      }
    }
  }

  setupVisibilityObserver() {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          this.isVisible = entry.isIntersecting;

          if (this.isVisible && !this.isPaused) {
            // First time becoming visible - start playing
            if (!this.hasStarted) {
              this.hasStarted = true;
              console.log("[VideoCarousel] Section visible, starting playback");
              this.playVideoAt(0);
            } else {
              // Returning to visibility - resume current video
              this.playVideoAt(this.currentIndex);
            }
            this.startAutoplay();
          } else {
            this.stopAutoplay();
            this.pauseAllVideos();
          }
        });
      },
      { threshold: 0.3 },
    );

    observer.observe(this.section);
  }

  startAutoplay() {
    this.stopAutoplay();
    if (this.isPaused || !this.isVisible) return;

    this.autoplayTimer = setInterval(() => {
      this.advanceToNext();
    }, this.duration);
  }

  stopAutoplay() {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  advanceToNext() {
    console.log("[VideoCarousel] advanceToNext", {
      currentIndex: this.currentIndex,
      slidesLength: this.slides.length,
    });

    if (this.slides.length === 0) return;

    const nextIndex = (this.currentIndex + 1) % this.slides.length;
    this.goToSlide(nextIndex);
  }

  goToSlide(index) {
    console.log("[VideoCarousel] goToSlide", {
      index,
      indexType: typeof index,
      carousel: this.carousel,
    });

    this.pauseAllVideos();
    this.currentIndex = index;

    if (this.carousel && typeof index === "number" && !isNaN(index)) {
      console.log(
        "[VideoCarousel] calling carousel.setSelectedIndex(" + index + ")",
      );
      this.carousel.setSelectedIndex(index);

      // Check if slide is fully visible, if not advance carousel
      const slide = this.slides[index];
      const tarotSlide = slide?.closest("tarot-slide");
      if (tarotSlide && !tarotSlide.classList.contains("tarot-fully-visible")) {
        console.log(
          "[VideoCarousel] slide not visible, calling carousel.next()",
        );
        this.carousel.next();
      }
    }

    this.playVideoAt(index);
  }

  playVideoAt(index) {
    const slide = this.slides[index];
    if (!slide) return;

    const video = slide.querySelector("video");
    const poster = slide.querySelector(".video-poster");

    if (video) {
      video.play().catch(() => {
        // Autoplay may be blocked, silently fail
      });
      // Hide poster when video plays
      if (poster) {
        poster.classList.add("opacity-0");
      }
    }
  }

  pauseAllVideos() {
    this.slides.forEach((slide) => {
      const video = slide.querySelector("video");
      const poster = slide.querySelector(".video-poster");
      if (video) {
        video.pause();
        video.currentTime = 0;
      }
      // Show poster when video pauses
      if (poster) {
        poster.classList.remove("opacity-0");
      }
    });
  }

  onSlideHover(index) {
    this.isHovering = true;
    this.stopAutoplay();
    this.pauseAllVideos();
    this.playVideoAt(index);
  }

  onSlideLeave() {
    this.isHovering = false;
    this.pauseAllVideos();
    this.playVideoAt(this.currentIndex);
    this.startAutoplay();
  }

  onSlideClick(index) {
    if (this.lightbox) {
      this.stopAutoplay();
      this.pauseAllVideos();
      this.lightbox.open(index);
    }
  }

  setLightbox(lightbox) {
    this.lightbox = lightbox;
  }

  pause() {
    this.isPaused = true;
    this.stopAutoplay();
    this.pauseAllVideos();
  }

  resume() {
    this.isPaused = false;
    if (this.isVisible) {
      this.playVideoAt(this.currentIndex);
      this.startAutoplay();
    }
  }
}

function init() {
  // Initialize lightbox
  const lightbox = new VideoLightbox();

  // Initialize carousel managers
  document.querySelectorAll(".video-carousel").forEach((section) => {
    const manager = new VideoCarouselManager(section);
    manager.setLightbox(lightbox);
    lightbox.registerCarouselManager(manager); // Register for pause/resume on lightbox open/close
    manager.init();
  });
}

// Initialize on DOM ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init);
} else {
  init();
}

export { init, VideoCarouselManager, VideoLightbox };
